/**
 * Game Health & Content screen — mirrors the four blocks the ops team
 * uses on the old admin dashboard, re-styled for the /admin/v2 dark UI:
 *
 *   1. Content library    · N,NNN PUBLISHED / N,NNN TOTAL + per-category bar list
 *      (source: GET /admin/metrics)
 *   2. Answer-position    · side-by-side A/B/C/D bars for all-clicks +
 *      distribution         correct-clicks — verifies the shuffle isn't biased
 *      (source: GET /admin/metrics/game-health · answer_position_distribution)
 *   3. Difficulty mix     · stacked bar + per-category table
 *      (published)          (source: GET /admin/metrics/content-quality · difficulty_mix)
 *   4. Fatigue (30d)      · 4 KPI cards
 *      (source: GET /admin/metrics/content-quality · fatigue)
 *
 * Three endpoints in parallel — a slow / broken one shows an empty
 * state on its section rather than tanking the whole page. Live-event
 * funnels, question-accuracy outliers and moderation flags moved to
 * their own screens; this page is content-quality only.
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import { errorState, escape, fetchOrNull, fmtInt, skeletonGrid } from './common.js';

const DIFF_COLOR = {
  easy: 'var(--a-ok)',
  medium: '#E5A630',
  hard: '#E94BA2',
};

export async function renderGame(host, ctx) {
  host.innerHTML = skeletonGrid(4);
  try {
    const [metrics, cq, gh] = await Promise.all([
      api('/admin/metrics'),
      fetchOrNull('/admin/metrics/content-quality'),
      fetchOrNull('/admin/metrics/game-health'),
    ]);
    host.innerHTML = _render(metrics, cq, gh);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

function _render(metrics, cq, gh) {
  return `
    ${_contentLibraryHtml(metrics)}
    ${_answerPositionHtml(gh)}
    ${_difficultyHtml(cq)}
    ${_fatigueHtml(cq)}
  `;
}

// ---------------------------------------------------------------------------
// 1. Content library — total banner + per-category bar list
// ---------------------------------------------------------------------------

function _contentLibraryHtml(metrics) {
  const q = metrics?.questions || {};
  const rows = q.by_category || [];
  const total = Number(q.total || 0);
  const published = Number(q.published || 0);

  if (!rows.length) {
    return `
      <div class="adm2-section-label">Content library</div>
      <div class="adm2-panel"><div class="adm2-kpi-hint">No categories yet.</div></div>
    `;
  }

  const max = Math.max(1, ...rows.map((r) => Number(r.total || 0)));
  const bars = rows
    .map((r) => {
      const pct = Math.round((Number(r.total || 0) / max) * 100);
      return `
        <div style="padding:14px 0;border-bottom:1px solid var(--a-border);">
          <div style="display:flex;align-items:baseline;justify-content:space-between;gap:10px;">
            <span style="font:600 15px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">${escape(r.name || r.slug || '(unnamed)')}</span>
            <span style="font:500 13px 'IBM Plex Mono',monospace;color:var(--a-muted);">${escape(fmtInt(r.published))} / ${escape(fmtInt(r.total))}</span>
          </div>
          <div style="margin-top:8px;height:6px;background:var(--a-track);border-radius:3px;overflow:hidden;">
            <div style="height:100%;width:${pct}%;background:linear-gradient(90deg,#5B2C91,#E94BA2);"></div>
          </div>
        </div>
      `;
    })
    .join('');

  return `
    <div class="adm2-panel" style="padding:20px 22px;">
      <div style="font:600 12px 'IBM Plex Sans',sans-serif;letter-spacing:.08em;text-transform:uppercase;color:var(--a-muted);margin-bottom:16px;">
        ${escape(fmtInt(published))} PUBLISHED / ${escape(fmtInt(total))} TOTAL
      </div>
      ${bars}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// 2. Answer-position distribution — All clicks + Correct clicks
// ---------------------------------------------------------------------------

function _answerPositionHtml(gh) {
  const p = gh?.answer_position_distribution;
  if (!p) return '';
  const note = escape(
    p.note ||
      'choice_index is POST-shuffle (the shuffle is per-user-per-question). Skew in `all_clicks` = user click bias; skew in `correct_clicks` = shuffle bias.',
  );
  return `
    <div class="adm2-section-label">Answer-position distribution</div>
    <div class="adm2-panel">
      <div class="adm2-two" style="gap:20px;">
        ${_positionsPanel('All clicks', p.all_clicks || {})}
        ${_positionsPanel('Correct clicks', p.correct_clicks || {})}
      </div>
      <div style="font:400 11px 'IBM Plex Sans',sans-serif;color:var(--a-faint);margin-top:14px;line-height:1.5;">
        ${note}
      </div>
    </div>
  `;
}

function _positionsPanel(title, bucket) {
  const total = Number(bucket.total || 0);
  const labels = ['A', 'B', 'C', 'D'];
  const bars = labels
    .map((label, i) => {
      const count = Number(bucket[String(i)] || 0);
      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
      // ~10% off the 25% ideal → outlier; colour the bar red instead of green.
      const tone = Math.abs(pct - 25) >= 10 ? 'var(--a-bad)' : 'var(--a-ok)';
      const heightPct = Math.max(6, Math.min(100, pct * 3));
      return `
        <div style="flex:1;text-align:center;">
          <div style="height:64px;display:flex;align-items:flex-end;justify-content:center;">
            <div title="${escape(label)}: ${count} (${pct}%)" style="width:70%;height:${heightPct}%;background:${tone};border-radius:5px 5px 0 0;opacity:.9;"></div>
          </div>
          <div style="font:500 11px 'IBM Plex Mono',monospace;color:var(--a-muted);margin-top:6px;">${escape(label)} · ${pct}%</div>
        </div>
      `;
    })
    .join('');
  return `
    <div>
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);margin-bottom:12px;">
        ${escape(title)} · ${escape(fmtInt(total))}N
      </div>
      <div style="display:flex;gap:8px;align-items:flex-end;">${bars}</div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// 3. Difficulty mix (published) — stacked bar + per-category table
// ---------------------------------------------------------------------------

function _difficultyHtml(cq) {
  const d = cq?.difficulty_mix;
  if (!d) return '';
  const t = d.totals || {};
  const total = Number(t.easy || 0) + Number(t.medium || 0) + Number(t.hard || 0);
  if (total === 0) {
    return `
      <div class="adm2-section-label">Content quality</div>
      <div class="adm2-panel">
        <div class="adm2-panel-title">Difficulty mix (published)</div>
        <div class="adm2-kpi-hint" style="margin-top:12px;">No published questions yet.</div>
      </div>
    `;
  }
  const pct = (n) => (Number(n || 0) / total) * 100;
  const stacked = `
    <div style="display:flex;height:14px;border-radius:7px;overflow:hidden;background:var(--a-track);">
      <div title="Easy ${t.easy}" style="width:${pct(t.easy)}%;background:${DIFF_COLOR.easy};"></div>
      <div title="Medium ${t.medium}" style="width:${pct(t.medium)}%;background:${DIFF_COLOR.medium};"></div>
      <div title="Hard ${t.hard}" style="width:${pct(t.hard)}%;background:${DIFF_COLOR.hard};"></div>
    </div>
    <div style="display:flex;gap:18px;flex-wrap:wrap;font:500 13px 'IBM Plex Sans',sans-serif;margin-top:12px;color:var(--a-muted);">
      <span><b style="color:${DIFF_COLOR.easy};">Easy</b> ${escape(fmtInt(t.easy))}</span>
      <span><b style="color:${DIFF_COLOR.medium};">Medium</b> ${escape(fmtInt(t.medium))}</span>
      <span><b style="color:${DIFF_COLOR.hard};">Hard</b> ${escape(fmtInt(t.hard))}</span>
      <span style="color:var(--a-faint);">${escape(fmtInt(total))} total</span>
    </div>
  `;

  const by = d.by_category || [];
  const header = `
    <thead>
      <tr>
        <th style="text-align:left;padding:12px 14px;font:600 11px 'IBM Plex Sans',sans-serif;color:var(--a-muted);letter-spacing:.06em;text-transform:uppercase;">Category</th>
        <th style="text-align:right;padding:12px 14px;font:600 11px 'IBM Plex Sans',sans-serif;color:var(--a-muted);letter-spacing:.06em;text-transform:uppercase;">Easy</th>
        <th style="text-align:right;padding:12px 14px;font:600 11px 'IBM Plex Sans',sans-serif;color:var(--a-muted);letter-spacing:.06em;text-transform:uppercase;">Med</th>
        <th style="text-align:right;padding:12px 14px;font:600 11px 'IBM Plex Sans',sans-serif;color:var(--a-muted);letter-spacing:.06em;text-transform:uppercase;">Hard</th>
        <th style="text-align:right;padding:12px 14px;font:600 11px 'IBM Plex Sans',sans-serif;color:var(--a-muted);letter-spacing:.06em;text-transform:uppercase;">Σ</th>
      </tr>
    </thead>
  `;
  const rows = by
    .map(
      (r) => `
      <tr>
        <td style="padding:11px 14px;font:500 13px 'IBM Plex Sans',sans-serif;color:var(--a-ink);border-top:1px solid var(--a-border);">
          ${escape(r.category_name || r.slug || '(unnamed)')}
        </td>
        <td style="padding:11px 14px;text-align:right;font-family:'IBM Plex Mono',monospace;color:${DIFF_COLOR.easy};border-top:1px solid var(--a-border);">${escape(fmtInt(r.easy))}</td>
        <td style="padding:11px 14px;text-align:right;font-family:'IBM Plex Mono',monospace;color:${DIFF_COLOR.medium};border-top:1px solid var(--a-border);">${escape(fmtInt(r.medium))}</td>
        <td style="padding:11px 14px;text-align:right;font-family:'IBM Plex Mono',monospace;color:${DIFF_COLOR.hard};border-top:1px solid var(--a-border);">${escape(fmtInt(r.hard))}</td>
        <td style="padding:11px 14px;text-align:right;font-family:'IBM Plex Mono',monospace;color:var(--a-ink);border-top:1px solid var(--a-border);">${escape(fmtInt(r.total))}</td>
      </tr>
    `,
    )
    .join('');

  return `
    <div class="adm2-section-label">Content quality</div>
    <div class="adm2-panel">
      <div style="font:600 12px 'IBM Plex Sans',sans-serif;letter-spacing:.08em;text-transform:uppercase;color:var(--a-muted);margin-bottom:14px;">
        Difficulty mix (published)
      </div>
      ${stacked}
      ${
        by.length
          ? `
        <div style="margin-top:18px;border:1px solid var(--a-border);border-radius:10px;overflow:hidden;">
          <table style="width:100%;border-collapse:collapse;">
            ${header}
            <tbody>${rows}</tbody>
          </table>
        </div>
      `
          : ''
      }
    </div>
  `;
}

// ---------------------------------------------------------------------------
// 4. Fatigue (30d) — 4 KPI cards
// ---------------------------------------------------------------------------

function _fatigueHtml(cq) {
  const f = cq?.fatigue;
  if (!f) return '';
  const rate = Number(f.rate || 0);
  const ratePct = `${(rate * 100).toFixed(1)}%`;
  const bad = rate < 0.3;
  const neverBad =
    Number(f.never_served_count || 0) > Math.max(10, Number(f.published_total || 0) * 0.3);

  const cards = `
    ${_fatigueCard({
      label: 'Served in 30d',
      value: ratePct,
      sub: `${fmtInt(f.served_in_window)} of ${fmtInt(f.published_total)}`,
      tone: bad ? 'var(--a-bad)' : 'var(--a-ok)',
    })}
    ${_fatigueCard({
      label: 'Never served',
      value: fmtInt(f.never_served_count),
      sub: 'published, times_served = 0',
      tone: neverBad ? 'var(--a-bad)' : 'var(--a-ink)',
    })}
    ${_fatigueCard({
      label: 'Median times served',
      value: fmtInt(f.times_served_median),
      sub: '',
      tone: 'var(--a-ink)',
    })}
    ${_fatigueCard({
      label: 'Max times served',
      value: fmtInt(f.times_served_max),
      sub: '',
      tone: 'var(--a-ink)',
    })}
  `;

  return `
    <div class="adm2-section-label">Fatigue (30d)</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(200px,1fr));">
      ${cards}
    </div>
    <div style="font:400 11px 'IBM Plex Sans',sans-serif;color:var(--a-faint);margin-top:12px;line-height:1.5;">
      "Served in 30d" counts distinct questions surfaced in any LiveTriviaAnswer in the window — solo/duel exposure not yet tracked here.
    </div>
  `;
}

function _fatigueCard({ label, value, sub, tone }) {
  return `
    <div style="background:var(--a-panel);border:1px solid var(--a-border);border-radius:14px;padding:20px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="font:700 34px 'IBM Plex Mono',monospace;color:${tone};line-height:1;">${escape(value)}</div>
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);margin-top:12px;">${escape(label)}</div>
      ${sub ? `<div style="font:400 11px 'IBM Plex Sans',sans-serif;color:var(--a-faint);margin-top:6px;">${escape(sub)}</div>` : ''}
    </div>
  `;
}
